import { useUserInfo } from "@features/auth/useAuth";
import { T } from "@i18n/T";
import { Button } from "@shadcn/ui/button";
import { Card } from "@shadcn/ui/card";
import { KeyboardAwareDrawer, KeyboardAwareDrawerContent } from "@components/KeyboardAwareDrawer";
import { DrawerTitle } from "@shadcn/ui/drawer";
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from "@stripe/react-stripe-js";
import { stripePromise } from "@utils/stripe";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { Check } from "lucide-react";
import { useState, useId } from "react";
import { graphql } from "react-relay";
import { toast } from "sonner";
import { z } from "zod";
import { PageContainer } from "~/components/layout/PageContainer";
import { useAsyncMutation } from "@hooks/useAsyncMutation";
import { subscriptionCreateMutation } from "./__generated__/subscriptionCreateMutation.graphql";

// Search params validation
const searchParamsSchema = z.object({
	returnUrl: z.string().optional(),
});

export const Route = createFileRoute("/subscription")({
	beforeLoad: () => {
		document.title = "bacehub | Startseite";
	},
	component: RouteComponent,
	validateSearch: zodValidator(searchParamsSchema),
});

const createSubscriptionMutation = graphql`
	mutation subscriptionCreateMutation($tier: SubscriptionTier!, $clientReferenceId: String!, $returnUrl: String!) {
		subscriptionCreate(input: { tier: $tier, clientReferenceId: $clientReferenceId, returnUrl: $returnUrl }) {
			clientSecret
		}
	}
`;

//https://www.figma.com/design/NBnn0MEdCHdbMj2LsWQqng/Consumer-App?node-id=677-24511&m=dev
export default function RouteComponent() {
	const navigate = useNavigate();
	const user = useUserInfo();
	const uniqueId = useId();
	const { returnUrl } = Route.useSearch();
	const [paymentDrawerOpen, setPaymentDrawerOpen] = useState(false);
	const [checkoutSecret, setCheckoutSecret] = useState<string | null>(null);
	const [clientReferenceId] = useState(() => `sub_${Date.now()}_${uniqueId}`);

	const [createSubscription, isCreating] = useAsyncMutation<subscriptionCreateMutation>(createSubscriptionMutation);

	// Subscription details - Standard tier (Free)
	const subscriptionPrice = 0; // in cents - FREE subscription
	const subscriptionTier = "STANDARD";

	const handleSubscribe = async () => {
		if (!user) {
			navigate({
				to: "/auth",
				search: {
					redirectToPath: "/subscription",
					show: "sign-in",
				},
			});
			return;
		}

		const successUrl = returnUrl || `${location.origin}/one-delivery`;

		try {
			const response = await createSubscription({
				variables: {
					tier: subscriptionTier,
					clientReferenceId,
					returnUrl: successUrl,
				},
			});
			setCheckoutSecret(response.subscriptionCreate.clientSecret);
			setPaymentDrawerOpen(true);
		} catch (error) {
			console.error(error);
			toast.error("Failed to create subscription. Please try again.");
		}
	};

	return (
		<PageContainer className="px-6 mt-2 bg-muted">
			<div className="flex max-w-xl flex-col items-center gap-4 text-center mx-auto">
				<h2 className="text-lg font-medium text-foreground">
					<T>Select a subscription plan - starting from 0€ per month</T>
				</h2>
				<p className="text-base text-muted-foreground">
					<T>Store, exchange, pick up – it has never been easier. With bace, you get exactly the access you need:</T>
				</p>
			</div>

			<div className="flex w-full flex-col items-center mt-10">
				<Card className="w-full p-6">
					<div className="flex flex-col gap-8">
						<div className="flex flex-col gap-6">
							<div className="flex flex-col gap-3">
								<h3 className="text-lg font-medium text-foreground">
									<T>Free</T>
								</h3>
								<p className="text-sm text-muted-foreground">
									<T>Our free account. Pay-as-you-go and pay at the end of the month. Set up your payment in the next step.</T>
								</p>
							</div>
							<div className="flex items-end gap-0.5">
								<span className="text-4xl font-medium text-foreground">0 €</span>
								<span className="text-base text-muted-foreground">/month</span>
							</div>
							<Button variant="secondary" size="lg" className="w-full" onClick={handleSubscribe} disabled={isCreating}>
								<T>Select plan</T>
							</Button>
						</div>
						<div className="flex flex-col gap-4">
							<p className="text-sm font-medium text-foreground">
								<T>What's included:</T>
							</p>
							<ul className="flex flex-col gap-3">
								<li className="flex items-center gap-3">
									<Check className="h-5 w-5 text-primary" />
									<span className="text-sm text-muted-foreground">
										<T>Access to all bace hubs</T>
									</span>
								</li>
								<li className="flex items-center gap-3">
									<Check className="h-5 w-5 text-primary" />
									<span className="text-sm text-muted-foreground">
										<T>Unlimited deliveries</T>
									</span>
								</li>
								<li className="flex items-center gap-3">
									<Check className="h-5 w-5 text-primary" />
									<span className="text-sm text-muted-foreground">
										<T>Flexible schedule</T>
									</span>
								</li>
							</ul>
						</div>
					</div>
				</Card>
			</div>

			{/* Stripe Payment Drawer */}
			<KeyboardAwareDrawer
				open={paymentDrawerOpen}
				onOpenChange={(open) => {
					setPaymentDrawerOpen(open);
				}}
			>
				<KeyboardAwareDrawerContent className="h-full" forceMount={true}>
					<DrawerTitle className="sr-only">Subscription Checkout</DrawerTitle>
					{checkoutSecret && (
						<EmbeddedCheckoutProvider
							stripe={stripePromise}
							options={{
								clientSecret: checkoutSecret,
								async onComplete() {
									toast.success("Subscription activated successfully!");
								},
							}}
						>
							<EmbeddedCheckout className="overflow-y-scroll" />
						</EmbeddedCheckoutProvider>
					)}
				</KeyboardAwareDrawerContent>
			</KeyboardAwareDrawer>
		</PageContainer>
	);
}
